/**
 * Cockpit's binding of the shared Service Desk surfaces to the api.
 *
 * `ServiceDeskClient` (from `@ampersandbrands/ui`) is the contract the Help
 * Center, ticket form, and "My requests" list talk through. This file supplies
 * the Cockpit implementation: every call goes to `/v1/servicedesk/...` with the
 * same cookie fetch layer as the rest of the app. The staff hooks live in
 * `useServiceDesk.ts` and reuse the helpers exported here.
 *
 * Every api route answers `{ data: ... }`; `unwrap` strips that envelope.
 */

import type {
  SdArticle,
  SdArticleSummary,
  SdAsset,
  SdAttachment,
  SdCategoryNode,
  SdComment,
  SdCreateTicketInput,
  SdFormField,
  SdPerson,
  SdReplyInput,
  SdStore,
  SdTicketDetail,
  SdTicketSummary,
  ServiceDeskClient,
} from '@ampersandbrands/ui';
import { apiGet, apiPost, API_BASE } from './api';
import { withApiImageSrc } from './richTextHtml';
import { uploadEditorImage } from './editorImages';

/** Base of every Service Desk route. */
export const SD_BASE = `${API_BASE}/v1/servicedesk`;

/**
 * The desk Cockpit works. IT is the only desk at launch; a desk picker can set
 * this later (the api scopes every staff route by the desk slug).
 */
export const SD_DESK = 'it';

/** `/v1/servicedesk/desks/it` */
export const sdDeskBase = (desk: string = SD_DESK): string => `${SD_BASE}/desks/${encodeURIComponent(desk)}`;

type Envelope<T> = { data: T };

export async function sdGet<T>(path: string): Promise<T> {
  return (await apiGet<Envelope<T>>(path)).data;
}
export async function sdPost<T>(path: string, body: unknown): Promise<T> {
  return (await apiPost<Envelope<T>>(path, body)).data;
}

/** Query string from a record, skipping empty values. */
export function sdQuery(params: Record<string, string | number | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/** Attachment urls come back host-relative; the browser needs the api host in front. */
export function sdAttachmentUrl(url: string): string {
  return url.startsWith('http') ? url : `${API_BASE}${url}`;
}

function fixAttachments<T extends { attachments: SdAttachment[] }>(item: T): T {
  return { ...item, attachments: item.attachments.map((a) => ({ ...a, url: sdAttachmentUrl(a.url) })) };
}

/** Prefix the api host on images inside a server-sanitized HTML body (articles, comments). */
export function sdHtml(html: string): string {
  return withApiImageSrc(html).replaceAll('src="/v1/servicedesk/', `src="${API_BASE}/v1/servicedesk/`);
}

/** The reverse of sdHtml (servicedesk and core editor images): store api-hosted images as relative paths so a body is not tied to one stage. */
export function sdHtmlForStorage(html: string): string {
  return html
    .replaceAll(`src="${API_BASE}/v1/servicedesk/`, 'src="/v1/servicedesk/')
    .replaceAll(`src="${API_BASE}/v1/core/images/`, 'src="/v1/core/images/');
}

function fixTicket(t: SdTicketDetail): SdTicketDetail {
  return {
    ...fixAttachments(t),
    description_html: t.description_html ? sdHtml(t.description_html) : t.description_html,
    comments: t.comments.map((c) => ({ ...fixAttachments(c), body_html: sdHtml(c.body_html) })),
  };
}

/** The Cockpit implementation of the shared client, using desk `it`. */
export const cockpitServiceDeskClient: ServiceDeskClient = {
  getCategoryTree: () => sdGet<SdCategoryNode[]>(`${sdDeskBase()}/categories`),
  getForm: (categoryId) => sdGet<SdFormField[]>(`${sdDeskBase()}/form${sdQuery({ category_id: categoryId })}`),
  listPeople: (scope, query) => sdGet<SdPerson[]>(`${SD_BASE}/people${sdQuery({ scope, q: query })}`),
  listStores: () => sdGet<SdStore[]>(`${SD_BASE}/stores`),
  getPersonAssets: (email) => sdGet<SdAsset[]>(`${SD_BASE}/people/${encodeURIComponent(email)}/assets`),
  createTicket: (input: SdCreateTicketInput) =>
    sdPost<SdTicketSummary>(`${SD_BASE}/tickets`, { ...input, desk: SD_DESK }),
  completeDetails: (ticketId, input) => sdPost<SdTicketSummary>(`${SD_BASE}/tickets/${ticketId}/details`, input),
  listMyTickets: (filter) => sdGet<SdTicketSummary[]>(`${SD_BASE}/me/tickets${sdQuery({ filter })}`),
  getTicket: async (ticketId) => fixTicket(await sdGet<SdTicketDetail>(`${SD_BASE}/tickets/${ticketId}`)),
  addReply: async (ticketId, input: SdReplyInput) => {
    const c = await sdPost<SdComment>(`${SD_BASE}/tickets/${ticketId}/replies`, input);
    return { ...fixAttachments(c), body_html: sdHtml(c.body_html) };
  },
  confirmResolved: async (ticketId, fixed, note) =>
    fixTicket(await sdPost<SdTicketDetail>(`${SD_BASE}/tickets/${ticketId}/confirm`, { fixed, note })),
  markRead: async (ticketId) => {
    await sdPost<{ ok: true }>(`${SD_BASE}/tickets/${ticketId}/read`, {});
  },
  decideApproval: async (ticketId, decision, reason) =>
    fixTicket(await sdPost<SdTicketDetail>(`${SD_BASE}/tickets/${ticketId}/approval`, { decision, reason })),
  uploadImage: uploadEditorImage,
  getUnreadCount: async () => (await sdGet<{ count: number }>(`${SD_BASE}/me/unread`)).count,
  listArticles: () => sdGet<SdArticleSummary[]>(`${SD_BASE}/articles`),
  searchArticles: (query, categoryId) =>
    sdGet<SdArticleSummary[]>(`${SD_BASE}/articles${sdQuery({ q: query, category_id: categoryId })}`),
  getArticle: async (articleId) => {
    const a = await sdGet<SdArticle>(`${SD_BASE}/articles/${articleId}`);
    return { ...a, html: sdHtml(a.html) };
  },
  getCategoryForPage: (pageKey) =>
    sdGet<Awaited<ReturnType<ServiceDeskClient['getCategoryForPage']>>>(
      `${sdDeskBase()}/categories/for-page${sdQuery({ key: pageKey })}`,
    ),
  recordArticleView: async (articleId) => {
    await sdPost<{ ok: true }>(`${SD_BASE}/articles/${articleId}/view`, {});
  },
};

/**
 * The Help Center article that was imported from a Confluence page, for a
 * `?help_article=<Confluence page id>` link. Keyed on the Confluence id so the
 * same link works on stage and prod, where article row ids differ. Null when no
 * published article matches or the lookup fails.
 */
export async function findArticleIdByConfluenceId(confluenceId: string): Promise<number | null> {
  if (!/^\d{1,32}$/.test(confluenceId)) return null;
  try {
    const a = await sdGet<{ id?: number }>(`${SD_BASE}/articles/by-legacy/${confluenceId}`);
    return typeof a?.id === 'number' ? a.id : null;
  } catch {
    return null;
  }
}
